import { useApolloClient } from '@apollo/client';
import {
  Box,
  Divider,
  Flex,
  Heading,
  IconButton,
  Text,
  Skeleton,
  useToast,
  Link,
  SimpleGrid,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Table,
  Tooltip,
  Center,
  Spacer,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Stack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useParams,
  useHistory,
  Link as ReactRouterLink,
} from 'react-router-dom';

import {
  borrowingBasePipeline as bbpl,
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';
import { progress } from '@finley-technologies/tape-builder';

import {
  DeliverableArtifactGQLType,
  GetDeliverableArtifactsByContainerIdDocumentGQLType,
  GetDeliverableContainerByIdDocumentGQLType,
  useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType,
  useGetDeliverableArtifactsByContainerIdQuery,
  useGetDeliverableContainerByIdQuery,
  useRefreshDeliverableMutation,
  useUpdateFailedDeliverableArtifactInstanceMutation,
  useCreateDeliverableArtifactInstanceMutation,
  RequiredDataTapeGQLType,
  useGetRequiredDataTapesQuery,
  DeliverableArtifactConfigGQLType,
  useGetBorrowingBasePipelineArtifactInstancesErrorsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';
import {
  CsvTape,
  CsvTapeState,
  DataTapeUiStatus,
} from 'types/manualDataTapeUpload';

import {
  ArtifactStatusCell,
  artifactStatusColor,
  calculateArtifactStatus,
  calculateStatus,
  statusColor,
  StatusIcon,
  UploadErrorCell,
  UploadProgressBar,
} from './Status';
import { Upload } from './Upload';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { directUpload } from 'packages/manualDataTapeUploads/ui/DataTapeContainer';
import { DataTapeErrorView } from 'packages/manualDataTapeUploads/ui/DataTapeErrorView';

// Used in progress bar to track upload progression.
export type DeliverableArtifactToDataTapeMap = {
  [key: string]: // keyed by deliverable artifact id
  | {
        loadingPercent: number;
        status: DataTapeUiStatus;
        bbplArtifactSlug: string;
      }
    | undefined;
};

const getCsvTapeState = ({
  tape,
  inProgressSlugs,
  completedSlugs,
}: {
  tape: RequiredDataTapeGQLType;
  inProgressSlugs: string[];
  completedSlugs: string[];
}): CsvTapeState => {
  const progressSummary: Util.Maybe<progress.ProgressSummary> =
    tape.progressSummary;
  let localStatus: DataTapeUiStatus;

  // calculate status
  if (
    inProgressSlugs.includes(tape.artifactSlug) ||
    progressSummary?.status === 'inProgress'
  ) {
    // TODO(jon): differentiate between `uploading` and `processing`.
    localStatus = 'uploading';
  } else if (progressSummary?.status === 'error') {
    localStatus = 'error';
  } else if (completedSlugs.includes(tape.artifactSlug)) {
    localStatus = 'success';
  } else {
    localStatus = 'none';
  }
  return {
    ...tape,
    fileType: 'csv', // TODO(jon): support xlsx
    key: tape.artifactSlug,
    errors: [],
    localStatus,
    progressSummary: tape.progressSummary ?? null,
  };
};

const UploadButton: React.FC<{
  creditFacilitySlug: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  fontAwesomeIcon: string;
  buttonText: string;
  iconOnly?: boolean;
  dataTape?: CsvTapeState;
  onSubmitDataTape: (
    file: File,
    tape: CsvTape,
    uploadReportingDate: Util.FullDateStr,
  ) => Promise<void>;
}> = ({
  creditFacilitySlug,
  creditFacilityId,
  organizationId,
  artifact,
  fontAwesomeIcon,
  buttonText,
  iconOnly,
  dataTape,
  onSubmitDataTape,
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  return (
    <>
      {iconOnly ? (
        <IconButton
          aria-label="Upload file"
          size="sm"
          icon={<Icon icon={fontAwesomeIcon} color="blue.400" />}
          variant="ghost"
          onClick={() => {
            setIsUploadModalOpen(true);
          }}
        />
      ) : (
        <Button
          color="blue.400"
          leftIcon={
            <Icon icon={fontAwesomeIcon} color="blue.400" boxSize={4} />
          }
          variant="ghost"
          fontSize="sm"
          py="2px"
          px="8px"
          onClick={() => {
            setIsUploadModalOpen(true);
          }}
        >
          {buttonText}
        </Button>
      )}
      <Upload
        creditFacilitySlug={creditFacilitySlug}
        creditFacilityId={creditFacilityId}
        organizationId={organizationId}
        artifact={artifact}
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        dataTape={dataTape}
        onSubmitDataTape={onSubmitDataTape}
      />
    </>
  );
};

const DownloadButton: React.FC<{
  fileName: string;
  artifactInstanceId: string;
}> = ({ fileName, artifactInstanceId }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [
    getDeliverableArtifactInstanceDownload,
  ] = useGetDeliverableArtifactInstanceDownloadLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (response && response.deliverableArtifactInstanceDownload) {
        const {
          deliverableArtifactInstanceDownload: downloadResponse,
        } = response;
        if (downloadResponse.__typename === 'ArtifactInstanceDownloadUrl') {
          const url = downloadResponse.signedUrl;
          axios({
            url,
            method: 'GET',
            responseType: 'blob',
          })
            .then((res) => {
              const fileBlob = new Blob([res.data], {
                type: res.data.type,
              });
              FileSaver.saveAs(fileBlob, fileName);
              successToastConfig.title = `Successfully downloaded ${fileName}`;
              toast(successToastConfig);
              setLoading(false);
            })
            .catch((err) => {
              setLoading(true);
              if (!toast.isActive(errorToastId)) {
                errorToastConfig.title = `Error downloading ${fileName}`;
                toast(errorToastConfig);
              }
              Sentry.captureException(err);
            });
        }
        if (downloadResponse.__typename === 'ArtifactInstanceDownloadObject') {
          const object = downloadResponse.object;
          const blob = new Blob([JSON.stringify(object)], {
            type: 'application/json',
          });
          const url = window.URL.createObjectURL(blob);

          const tag = document.createElement('a');
          tag.setAttribute('href', url);
          tag.setAttribute('download', `${fileName}.json`);
          tag.click();
          setLoading(false);
        }
      }
    },
  });

  return (
    <Button
      isLoading={loading}
      color="blue.400"
      leftIcon={<Icon icon="download" color="blue.400" boxSize={4} />}
      variant="ghost"
      fontSize="sm"
      py="2px"
      px="8px"
      onClick={() => {
        setLoading(true);
        Sentry.addBreadcrumb({
          category: 'ui.click',
          message: `Clicked download for ${fileName} artifacts instance in v4 deliverables detail view`,
          level: Sentry.Severity.Info,
        });
        getDeliverableArtifactInstanceDownload({
          variables: {
            artifactInstanceId,
          },
        });
      }}
    >
      Download
    </Button>
  );
};

const DrawerView: React.FC<{
  creditFacilitySlug: string;
  creditFacilityId: string;
  organizationId: string;
  artifact: DeliverableArtifactGQLType;
  isOpen: boolean;
  onClose: () => void;
  dataTape?: CsvTapeState;
  onSubmitDataTape: (params: {
    file: File;
    tape: CsvTape;
    deliverableArtifactId: string;
    uploadReportingDate: Util.FullDateStr;
  }) => Promise<void>;
  // errors should only exist for BBPL types (loan tape, repayment tape, supplemental tape)
  // if errors is undefined, there are no errors for this artifact
  // if errors exist but errors.errors is an empty array, that means there was an error but we do not have additional info
  // if errors.errors isn't empty, then we show the data tape errors in the field below
  errors?: {
    errors: progress.AggregatedError[];
    deliverableArtifactInstanceId: string;
  };
}> = ({
  creditFacilitySlug,
  creditFacilityId,
  organizationId,
  artifact,
  isOpen,
  onClose,
  dataTape,
  onSubmitDataTape,
  errors,
}) => {
  const toast = useToast();

  const [
    updateFailedDeliverableArtifactInstanceMutation,
  ] = useUpdateFailedDeliverableArtifactInstanceMutation({
    onCompleted: () => {
      toast({
        title: 'Marked deliverable artifact file version as error.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title:
          'Unable to mark deliverable artifact file version as error. Please contact Finley support.',
        status: 'error',
        position: 'top',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId: artifact.containerId },
      },
    ],
  });

  const status = calculateArtifactStatus(artifact);
  const nonErrorInstances = errors
    ? artifact.instances.filter(
        (i) => i.id !== errors.deliverableArtifactInstanceId,
      )
    : artifact.instances;
  return (
    <Drawer onClose={onClose} isOpen={isOpen} size="lg" closeOnOverlayClick>
      <DrawerOverlay bgColor="inherit" />
      <DrawerContent bgColor={colors.background[50]}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" mb={4}>{`${
          artifact.reportingDate
        } ${commonUtils.toTitleCase(
          artifact.config.artifactType,
        )}`}</DrawerHeader>
        <DrawerBody>
          <Stack spacing="20px" mb="20px">
            <HStack>
              <Text as="b" width="20%" fontSize="sm">
                Status
              </Text>
              <HStack
                px="12px"
                py="8px"
                borderRadius="8px"
                bgColor={
                  errors ? 'red.100' : `${artifactStatusColor[status]}.100`
                }
                width="fit-content"
              >
                {errors ? (
                  <UploadErrorCell isTextColored />
                ) : (
                  <ArtifactStatusCell status={status} isTextColored />
                )}
              </HStack>
            </HStack>

            {/* Show errors here if they exist */}
            {!!errors && errors.errors.length > 0 && (
              <DataTapeErrorView errors={errors.errors} numOfRows={8} />
            )}

            {(nonErrorInstances.length === 0 || errors) && (
              <HStack>
                <Text as="b" width="20%" fontSize="sm">
                  Action
                </Text>
                <UploadButton
                  creditFacilityId={creditFacilityId}
                  creditFacilitySlug={creditFacilitySlug}
                  organizationId={organizationId}
                  artifact={artifact}
                  fontAwesomeIcon="arrow-up-from-bracket"
                  buttonText="Upload"
                  dataTape={dataTape}
                  onSubmitDataTape={(file, tape, uploadReportingDate) =>
                    onSubmitDataTape({
                      file,
                      tape,
                      deliverableArtifactId: artifact.id,
                      uploadReportingDate,
                    })
                  }
                />
              </HStack>
            )}

            {nonErrorInstances.length > 0 && (
              <HStack alignItems="flex-start">
                <Text as="b" width="20%" fontSize="sm" mt={2}>
                  Files
                </Text>
                <Stack spacing="0px">
                  {nonErrorInstances.map((instance) => {
                    return (
                      <>
                        <SimpleGrid
                          key={instance.id}
                          columns={4}
                          spacing="10px"
                          alignItems="center"
                        >
                          <Text
                            color="gray.600"
                            fontSize="sm"
                            pr={4}
                          >{`Version ${instance.version}`}</Text>
                          <Text color="gray.600" fontSize="sm" pr={4}>
                            submitted date
                          </Text>
                          {instance.status === 'success' && (
                            <DownloadButton
                              fileName={`${artifact.config.artifactType}-${artifact.configKey}-${artifact.reportingDate}-v${instance.version}`}
                              artifactInstanceId={instance.id}
                            />
                          )}
                          {instance.status === 'error' && (
                            <Text
                              color="red.400"
                              fontSize="sm"
                              fontWeight="semibold"
                              pl={4}
                            >
                              File errored.
                            </Text>
                          )}
                          {instance.active && instance.status === 'inProgress' && (
                            <Tooltip
                              label="Cancel file processing and mark as error."
                              placement="top"
                            >
                              <Button
                                colorScheme="red"
                                color="red.400"
                                leftIcon={
                                  <Icon
                                    icon="circle-xmark"
                                    color="red.400"
                                    boxSize={4}
                                  />
                                }
                                variant="ghost"
                                fontSize="sm"
                                py="2px"
                                px="8px"
                                onClick={async () => {
                                  // update deliverable artifact instance from 'inProgress' -> 'error'
                                  await updateFailedDeliverableArtifactInstanceMutation(
                                    {
                                      variables: {
                                        instanceId: instance.id,
                                      },
                                    },
                                  );
                                  onClose();
                                }}
                              >
                                Cancel
                              </Button>
                            </Tooltip>
                          )}
                          {instance.active &&
                          instance.status !== 'inProgress' ? (
                            <UploadButton
                              creditFacilityId={creditFacilityId}
                              creditFacilitySlug={creditFacilitySlug}
                              organizationId={organizationId}
                              artifact={artifact}
                              fontAwesomeIcon="arrows-rotate"
                              buttonText="Overwrite"
                              dataTape={dataTape}
                              onSubmitDataTape={(
                                file,
                                tape,
                                uploadReportingDate,
                              ) =>
                                onSubmitDataTape({
                                  file,
                                  tape,
                                  deliverableArtifactId: artifact.id,
                                  uploadReportingDate,
                                })
                              }
                            />
                          ) : (
                            <Box />
                          )}
                        </SimpleGrid>
                      </>
                    );
                  })}
                </Stack>
              </HStack>
            )}

            <HStack>
              <Text as="b" width="20%" fontSize="sm">
                Submitted By
              </Text>
              <Text fontSize="sm" color="gray.600">
                -
              </Text>
            </HStack>
            <HStack>
              <Text as="b" width="20%" fontSize="sm">
                Submitted Date
              </Text>
              <Text fontSize="sm" color="gray.600">
                -
              </Text>
            </HStack>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const FinalArtifactsModal: React.FC<{
  containerId: string;
  creditFacilitySlug: string;
  creditFacilityId: string;
  organizationId: string;
  artifacts: DeliverableArtifactGQLType[];
  isOpen: boolean;
  onClose: () => void;
  getDataTape: (
    artifactConfig: DeliverableArtifactConfigGQLType,
  ) => CsvTapeState | undefined;
  onSubmitDataTape: (params: {
    file: File;
    tape: CsvTape;
    deliverableArtifactId: string;
    uploadReportingDate: Util.FullDateStr;
  }) => Promise<void>;
}> = ({
  containerId,
  creditFacilitySlug,
  creditFacilityId,
  organizationId,
  artifacts,
  isOpen,
  onClose,
  getDataTape,
  onSubmitDataTape,
}) => {
  const toast = useToast();

  const [
    updateFailedDeliverableArtifactInstanceMutation,
  ] = useUpdateFailedDeliverableArtifactInstanceMutation({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      toast({
        title: 'Marked deliverable artifact file version as error.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title:
          'Unable to mark deliverable artifact file version as error. Please contact Finley support.',
        status: 'error',
        position: 'top',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetDeliverableArtifactsByContainerIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId },
      },
      {
        query: GetDeliverableContainerByIdDocumentGQLType,
        fetchPolicy: 'network-only',
        variables: { containerId },
      },
    ],
  });

  const anyComplete = artifacts.some((a) => a.instances.length > 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={anyComplete ? '4xl' : 'md'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Download Report(s)</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {artifacts.map((artifact) => {
            return (
              <Stack key={artifact.id}>
                {artifact.instances.length > 0 && (
                  <>
                    <HStack alignItems="flex-start">
                      <Text as="b" width="40%" fontSize="sm" mt={3}>
                        {commonUtils.toTitleCase(artifact.config.artifactType)}
                      </Text>
                      <Stack spacing="0px">
                        {artifact.instances.map((instance) => {
                          return (
                            <SimpleGrid
                              key={instance.id}
                              columns={4}
                              spacing="10px"
                              alignItems="center"
                            >
                              <Text
                                color="gray.600"
                                fontSize="sm"
                                pr={4}
                              >{`Version ${instance.version}`}</Text>
                              <Text color="gray.600" fontSize="sm" pr={4}>
                                submitted date
                              </Text>
                              {instance.status === 'success' && (
                                <DownloadButton
                                  fileName={`${artifact.config.artifactType}-${artifact.configKey}-${artifact.reportingDate}-v${instance.version}`}
                                  artifactInstanceId={instance.id}
                                />
                              )}
                              {instance.status === 'error' && (
                                <Text
                                  color="red.400"
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  pl={4}
                                >
                                  File errored.
                                </Text>
                              )}
                              {instance.active &&
                                instance.status === 'inProgress' && (
                                  <Tooltip
                                    label="Cancel file processing and mark as error."
                                    placement="top"
                                  >
                                    <Button
                                      colorScheme="red"
                                      color="red.400"
                                      leftIcon={
                                        <Icon
                                          icon="circle-xmark"
                                          color="red.400"
                                          boxSize={4}
                                        />
                                      }
                                      variant="ghost"
                                      fontSize="sm"
                                      py="2px"
                                      px="8px"
                                      onClick={async () => {
                                        // update deliverable artifact instance from 'inProgress' -> 'error'
                                        await updateFailedDeliverableArtifactInstanceMutation(
                                          {
                                            variables: {
                                              instanceId: instance.id,
                                            },
                                          },
                                        );
                                        onClose();
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </Tooltip>
                                )}
                              {instance.active &&
                              instance.status !== 'inProgress' ? (
                                <UploadButton
                                  creditFacilityId={creditFacilityId}
                                  creditFacilitySlug={creditFacilitySlug}
                                  organizationId={organizationId}
                                  artifact={artifact}
                                  fontAwesomeIcon="arrows-rotate"
                                  buttonText="Regenerate"
                                  dataTape={getDataTape(artifact.config)}
                                  onSubmitDataTape={(
                                    file,
                                    tape,
                                    uploadReportingDate,
                                  ) =>
                                    onSubmitDataTape({
                                      file,
                                      tape,
                                      deliverableArtifactId: artifact.id,
                                      uploadReportingDate,
                                    })
                                  }
                                />
                              ) : (
                                <Box />
                              )}
                            </SimpleGrid>
                          );
                        })}
                      </Stack>
                    </HStack>
                  </>
                )}
                {artifact.instances.length === 0 && (
                  <Flex alignItems="center" mt={1}>
                    <Text as="b" fontSize="sm">
                      {commonUtils.toTitleCase(artifact.config.artifactType)}
                    </Text>
                    <Spacer />
                    <UploadButton
                      creditFacilityId={creditFacilityId}
                      creditFacilitySlug={creditFacilitySlug}
                      organizationId={organizationId}
                      artifact={artifact}
                      fontAwesomeIcon="arrows-rotate"
                      buttonText="Generate"
                      dataTape={getDataTape(artifact.config)}
                      onSubmitDataTape={(file, tape, uploadReportingDate) =>
                        onSubmitDataTape({
                          file,
                          tape,
                          deliverableArtifactId: artifact.id,
                          uploadReportingDate,
                        })
                      }
                    />
                  </Flex>
                )}
                <Divider />
              </Stack>
            );
          })}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const DeliverableContainerDetailView: React.FC<{
  organizationId: string;
  orgSlug: string;
}> = ({ organizationId, orgSlug }) => {
  const { deliverableId } = useParams<{ deliverableId: string }>();

  const history = useHistory();
  const toast = useToast();
  const client = useApolloClient();

  finleyAssert(deliverableId, 'deliverableId is required');

  const {
    data: containerData,
    loading: containerLoading,
    error: containerError,
    refetch: refetchContainer,
  } = useGetDeliverableContainerByIdQuery({
    fetchPolicy: 'network-only',
    variables: { containerId: deliverableId },
  });

  const {
    data: artifactsData,
    loading: artifactsLoading,
    error: artifactsError,
    refetch: refetchArtifacts,
  } = useGetDeliverableArtifactsByContainerIdQuery({
    fetchPolicy: 'network-only',
    variables: { containerId: deliverableId },
  });

  // maps bbpl artifact instance id to deliverable artifact id & instance id (for any inProgress/valid ones)
  const bbplInstanceToDeliverableArtifactMap = useMemo(() => {
    const mappedVals: Record<
      string,
      { artifactId: string; instanceId: string }
    > = {};

    if (artifactsData) {
      for (const deliverableArtifact of artifactsData.getDeliverableArtifactsByContainerId) {
        const deliverableActiveInstance = deliverableArtifact.instances.find(
          (i) => !!i.active,
        );
        if (
          deliverableActiveInstance &&
          deliverableActiveInstance.status === 'inProgress' &&
          deliverableActiveInstance.rawDatabase === 'pg' &&
          deliverableActiveInstance.rawNamespace ===
            'borrowing_base_pipeline_artifact_instances' &&
          deliverableActiveInstance.rawIdentifier
        ) {
          const bbplArtifactInstanceId = bbpl.parseArtifactInstancePgId(
            deliverableActiveInstance.rawIdentifier,
          );
          if (bbplArtifactInstanceId) {
            mappedVals[bbplArtifactInstanceId] = {
              artifactId: deliverableArtifact.id,
              instanceId: deliverableActiveInstance.id,
            };
          }
        }
      }
    }

    return mappedVals;
  }, [artifactsData]);

  const {
    data: errorsData,
  } = useGetBorrowingBasePipelineArtifactInstancesErrorsQuery({
    fetchPolicy: 'network-only',
    pollInterval: _.isEmpty(bbplInstanceToDeliverableArtifactMap) ? 0 : 60000, // don't poll if its empty, otherwise poll every minute
    variables: {
      rawArtifactInstanceIds: Object.keys(bbplInstanceToDeliverableArtifactMap),
      organizationId,
    },
  });

  // Map from deliverable artifact id to bbpl errors.
  // An empty error array indicates an error is present, but we don't have specific details.
  const errorsMap = useMemo(() => {
    const errMap: Record<
      string,
      {
        errors: progress.AggregatedError[];
        deliverableArtifactInstanceId: string;
      }
    > = {};
    if (errorsData) {
      for (const err of errorsData.borrowingBasePipelineDataValidationErrors) {
        const deliverableArtifact =
          bbplInstanceToDeliverableArtifactMap[err.artifactInstanceId];
        if (deliverableArtifact) {
          const errors: progress.AggregatedError[] = err.progressBreakdown.flatMap(
            (e) => e.status.errors ?? [],
          );

          errMap[deliverableArtifact.artifactId] = {
            errors,
            deliverableArtifactInstanceId: deliverableArtifact.instanceId,
          };
        }
      }
    }
    return errMap;
  }, [bbplInstanceToDeliverableArtifactMap, errorsData]);

  const [
    refreshDeliverableMutation,
    { loading: refreshLoading },
  ] = useRefreshDeliverableMutation({
    fetchPolicy: 'network-only',
    variables: {
      deliverableContainerId: deliverableId,
    },
    onCompleted: () => {
      refetchContainer();
      refetchArtifacts();
    },
  });

  // executes refresh mutation on page load
  useEffect(() => {
    refreshDeliverableMutation();
  }, [deliverableId, refreshDeliverableMutation]);

  const [
    createDeliverableArtifactInstanceMutation,
  ] = useCreateDeliverableArtifactInstanceMutation({
    fetchPolicy: 'network-only',
  });

  const { data: requiredDataTapesData } = useGetRequiredDataTapesQuery({
    variables: { organizationId },
    fetchPolicy: 'network-only',
  });

  const [
    drawerArtifact,
    setDrawerArtifact,
  ] = useState<DeliverableArtifactGQLType>();
  const [isFinalArtifactsOpen, setIsFinalArtifactsOpen] = useState<boolean>(
    false,
  );

  // Track upload progression by deliverable artifact id
  const [
    deliverableArtifactToDataTapeMap,
    setDeliverableArtifactToDataTapeMap,
  ] = useState<DeliverableArtifactToDataTapeMap>({});

  // Slugs for data tapes for which uploads are in progress.
  const [inProgressSlugs, setInProgressSlugs] = useState<string[]>([]);

  // Slugs for uploads that have been completed.
  const [completedSlugs, setCompletedSlugs] = useState<string[]>([]);

  // If data tape upload is in progress: Display browser warning popup when user attempts to manually refresh or close page
  useEffect(() => {
    const alertCallback = (event: Event) => {
      // using all 3 methods of beforeunload activation to cover most browser cases
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = true;
      return '';
    };

    const isUploadInProgress = Object.values(
      deliverableArtifactToDataTapeMap,
    ).some((val) => val?.status === 'uploading');

    if (isUploadInProgress) {
      window.addEventListener('beforeunload', alertCallback);
    }

    // remove exit prompt event listener when unmounting
    return () => {
      window.removeEventListener('beforeunload', alertCallback);
    };
  }, [deliverableArtifactToDataTapeMap]);

  if (
    !requiredDataTapesData ||
    !containerData ||
    !containerData.deliverableContainer ||
    !artifactsData ||
    !artifactsData.getDeliverableArtifactsByContainerId
  ) {
    return (
      <Skeleton height="600px" data-testid="deliverables-detail-view-loading" />
    );
  }

  if (containerError || artifactsError) {
    toast({ title: `Failed to load page.` });
    history.push('/deliverables');
  }

  const container = containerData.deliverableContainer;
  const artifacts = artifactsData.getDeliverableArtifactsByContainerId;
  const dependencies = artifacts.filter((a) => a.inputType === 'input');
  const finalArtifacts = artifacts.filter((a) => a.inputType === 'output');

  const title = `${commonUtils.toTitleCase(
    orgSlug,
  )} - ${commonUtils.toTitleCase(container.type)}`;

  const calculatedStatus = calculateStatus(container);

  /**
   * Everything below is to manage state for data tape uploads across modals, drawers, table views.
   * We cannot manage state across all these components when they are closed, so managing at the top level here.
   * 'uploadFile' puts the file into s3 and initiates polling for potential errors until files have reached the sanitized node.
   */

  // Create a map by loanProductSlugs -> dataTape for any that have them (see `loanProductSlugMap` below)
  // Create a map for all requiredDataTapes by bbplArtifactSlug (see `bbplArtifactSlugMap` below)
  const dataTapesWithLoanProductSlugs: RequiredDataTapeGQLType[] = [];
  const requiredDataTapes = requiredDataTapesData.getRequiredDataTapes.requiredDataTapesGroupings.flatMap(
    (group) => group.requiredDataTapes,
  );
  for (const tape of requiredDataTapes) {
    if (tape.loanProductSlug) {
      dataTapesWithLoanProductSlugs.push(tape);
    }
  }

  const loanProductSlugMap = _.groupBy(
    dataTapesWithLoanProductSlugs,
    (lp) => lp.loanProductSlug!, // we know that loanProductSlug will always exist due to for loop above.
  );

  const bbplArtifactSlugMap = _.groupBy(
    requiredDataTapes,
    (lp) => lp.artifactSlug,
  );

  const getDataTape = (
    artifactConfig: DeliverableArtifactConfigGQLType,
  ): CsvTapeState | undefined => {
    let tapes: RequiredDataTapeGQLType[] | undefined;
    let tape: RequiredDataTapeGQLType | undefined;

    if (artifactConfig.__typename === 'SupplementalTapeConfig') {
      tapes = bbplArtifactSlugMap[artifactConfig.artifactSlug];
    } else if (
      artifactConfig.__typename === 'OrgProvidedInputConfig' &&
      !!artifactConfig.bbplArtifactSlug
    ) {
      tapes = bbplArtifactSlugMap[artifactConfig.bbplArtifactSlug];
    } else if (
      artifactConfig.__typename === 'LoanTapeConfig' ||
      artifactConfig.__typename === 'RepaymentTapeConfig'
    ) {
      tapes = loanProductSlugMap[artifactConfig.loanProductSlug];
    } else {
      // if wrong type, return undefined (makes it impossible to upload)
      return undefined;
    }

    if (tapes && tapes.length > 0) {
      // find a tape with a valid credit facility id
      tape = tapes.find((t) =>
        t.validCreditFacilityIds.includes(container.creditFacilityId),
      );
    }

    // for supplemental nodes, `validCreditFacilityIds` will usually be an empty list
    if (
      !tape &&
      tapes.length === 1 &&
      tapes[0].validCreditFacilityIds.length === 0
    ) {
      tape = tapes[0];
    }

    if (tape) {
      const result = getCsvTapeState({
        tape,
        inProgressSlugs,
        completedSlugs,
      });
      return result;
    }
    return undefined;
  };

  // Data tape upload file function
  const uploadFile = async ({
    file,
    tape,
    deliverableArtifactId,
    uploadReportingDate,
  }: {
    file: File;
    tape: CsvTape;
    deliverableArtifactId: string;
    uploadReportingDate: Util.FullDateStr;
  }): Promise<void> => {
    const dataTapeArtifactSlug = tape.artifactSlug;

    if (inProgressSlugs.includes(dataTapeArtifactSlug)) return;

    // Reset error state and display loading icon instead of upload button
    setInProgressSlugs((prevState) => [...prevState, dataTapeArtifactSlug]);
    // Remove slug from completed
    setCompletedSlugs((prevState) =>
      prevState.filter((slug) => slug !== dataTapeArtifactSlug),
    );
    setDeliverableArtifactToDataTapeMap((prevState) => ({
      ...prevState,
      [deliverableArtifactId]: {
        loadingPercent: 0,
        status: 'none',
        bbplArtifactSlug: dataTapeArtifactSlug,
      },
    }));

    // TODO(jon): support error reporting for direct s3 upload.
    let status: DataTapeUiStatus = 'none';
    let loadingPercent: number = 0;
    try {
      const bbplInstanceId = await directUpload({
        client,
        file,
        organizationId,
        artifactId: tape.artifactId,
        reportingDate: uploadReportingDate,
        onEventCallback: (loaded, total) => {
          const percentCompleted = Math.ceil((loaded * 100) / total);
          setDeliverableArtifactToDataTapeMap((prevState) => ({
            ...prevState,
            [deliverableArtifactId]: {
              loadingPercent: percentCompleted,
              status: 'uploading',
              bbplArtifactSlug: dataTapeArtifactSlug,
            },
          }));
        },
      });

      await createDeliverableArtifactInstanceMutation({
        variables: {
          deliverableArtifactId,
          active: true,
          status: 'inProgress',
          rawDatabase: 'pg',
          rawNamespace: 'borrowing_base_pipeline_artifact_instances',
          rawIdentifier: bbpl.generateArtifactInstancePgIdentifer({
            column: 'id',
            id: bbplInstanceId,
          }),
        },
      });
      refetchArtifacts();
      refetchContainer();

      // remove in progress
      setInProgressSlugs((prevState) =>
        prevState.filter((slug) => slug !== dataTapeArtifactSlug),
      );

      // mark as completed
      setCompletedSlugs((prevState) => [...prevState, dataTapeArtifactSlug]);

      status = 'success';
      loadingPercent = 100;
    } catch (err: any) {
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing upload. Please try again, and contact Finley support if this persists.',
      });
      // remove in progress
      setInProgressSlugs((prevState) =>
        prevState.filter((slug) => slug !== dataTapeArtifactSlug),
      );

      // If somehow slug is in completed, removed from completed & mark as error here
      setCompletedSlugs((prevState) =>
        prevState.filter((slug) => slug !== dataTapeArtifactSlug),
      );

      status = 'error';
      loadingPercent = 0;
    } finally {
      setDeliverableArtifactToDataTapeMap((prevState) => ({
        ...prevState,
        [deliverableArtifactId]: {
          loadingPercent,
          status,
          bbplArtifactSlug: dataTapeArtifactSlug,
        },
      }));
    }
  };

  return (
    <Box height="100%">
      <Flex justify="space-between" flexDirection="column">
        <Text fontSize="xs" color="gray.800" letterSpacing="wide" pb="16px">
          <Link as={ReactRouterLink} to="/deliverables" fontWeight="semibold">
            Deliverables
          </Link>{' '}
          {`> ${title}`}
        </Text>
        <HStack pb="16px" spacing={4}>
          <Heading as="h2" fontSize="3xl">
            {title}
          </Heading>
          <Center>
            <Tooltip bg="gray.500" label="Refresh data" placement="right">
              <IconButton
                isLoading={
                  refreshLoading || containerLoading || artifactsLoading
                }
                aria-label="Refresh data"
                size="lg"
                icon={
                  <Icon icon="arrows-rotate" size="large" color="blue.500" />
                }
                variant="ghost"
                onClick={async () => {
                  await refreshDeliverableMutation();
                }}
              />
            </Tooltip>
          </Center>
        </HStack>
        <Divider mb={8} />
        <Flex>
          <HStack
            padding="12px"
            borderRadius="8px"
            bgColor={`${statusColor[calculatedStatus]}.100`}
            width="fit-content"
          >
            <StatusIcon status={calculatedStatus} />
            <Text
              fontWeight="semibold"
              color={`${statusColor[calculatedStatus]}.300`}
            >
              {commonUtils.toTitleCase(calculatedStatus)}
            </Text>
          </HStack>
          <Spacer />
          {finalArtifacts &&
            // if final artifact(s) exist & all dependencies are complete show download/generate modal
            _.every(
              dependencies,
              (d) => calculateArtifactStatus(d) === 'submitted',
            ) && (
              <Button
                leftIcon={<Icon icon="download" color="white" />}
                py={5}
                justifyContent="flex-start"
                variant="solid"
                size="sm"
                bgColor={colors.aurora[400]}
                color="white"
                onClick={() => {
                  setIsFinalArtifactsOpen(true);
                }}
              >
                Output Report(s)
              </Button>
            )}
        </Flex>
        <FinalArtifactsModal
          containerId={container.id}
          creditFacilitySlug={container.creditFacilitySlug}
          creditFacilityId={container.creditFacilityId}
          organizationId={organizationId}
          artifacts={finalArtifacts}
          isOpen={isFinalArtifactsOpen}
          onClose={() => setIsFinalArtifactsOpen(false)}
          getDataTape={getDataTape}
          onSubmitDataTape={uploadFile}
        />
        <SimpleGrid columns={3} spacingX={10} spacingY="4px" mt={8} mb={6}>
          <Text fontSize="xs" color="gray.600">
            Credit Facility
          </Text>
          <Text fontSize="xs" color="gray.600">
            Deliverable Type
          </Text>
          <Text fontSize="xs" color="gray.600">
            Due Date
          </Text>

          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(container.creditFacilitySlug)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonUtils.toTitleCase(container.type)}
          </Text>
          <Text as="b" fontSize="sm">
            {commonDate.fullDateStrToHumanDateString(container.dueDate)}
          </Text>
        </SimpleGrid>

        <Tabs size="sm">
          <TabList>
            <Tab>Required Files</Tab>
            <Tab>Details</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <Box overflowY="auto">
                <Table variant="simple">
                  <Thead fontSize="md" fontWeight="bold">
                    <Tr>
                      <Th>Status</Th>
                      <Th>Name</Th>
                      <Th>Type</Th>
                      <Th>Reporting Date</Th>
                      {/* SARAH TODO: revisit when upload is supported: <Th>Submitted By</Th> */}
                      <Th>Submitted Date</Th>
                      <Th>Action</Th>
                      <Th />
                    </Tr>
                  </Thead>
                  <Tbody fontSize="sm">
                    {dependencies.map((d) => {
                      const progressVal =
                        deliverableArtifactToDataTapeMap[d.id];
                      const errVal:
                        | {
                            errors: progress.AggregatedError[];
                            deliverableArtifactInstanceId: string;
                          }
                        | undefined = errorsMap[d.id];
                      return (
                        <>
                          <Tr key={d.id}>
                            <Td>
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {errVal ? (
                                <UploadErrorCell isTextColored={false} />
                              ) : progressVal?.status === 'uploading' ? (
                                <UploadProgressBar
                                  progressValue={progressVal.loadingPercent}
                                />
                              ) : (
                                <ArtifactStatusCell
                                  status={calculateArtifactStatus(d)}
                                  isTextColored={false}
                                />
                              )}
                            </Td>
                            <Td>{commonUtils.toTitleCase(d.configKey)}</Td>
                            <Td>
                              {commonUtils.toTitleCase(d.config.artifactType)}
                            </Td>
                            <Td>
                              {commonDate.fullDateStrToHumanDateString(
                                d.reportingDate,
                              )}
                            </Td>
                            {/* SARAH TODO: submitted date */}
                            <Td>-</Td>
                            <Td>
                              {calculateArtifactStatus(d) === 'processing' &&
                              !errVal ? (
                                <Tooltip
                                  label="Processing current upload."
                                  aria-label="Upload tooltip"
                                >
                                  <Text ml={3}>-</Text>
                                </Tooltip>
                              ) : (
                                <UploadButton
                                  creditFacilityId={container.creditFacilityId}
                                  creditFacilitySlug={
                                    container.creditFacilitySlug
                                  }
                                  organizationId={organizationId}
                                  artifact={d}
                                  fontAwesomeIcon="arrow-up-from-bracket"
                                  buttonText="Upload"
                                  iconOnly
                                  dataTape={getDataTape(d.config)}
                                  onSubmitDataTape={(
                                    file,
                                    tape,
                                    uploadReportingDate,
                                  ) =>
                                    uploadFile({
                                      file,
                                      tape,
                                      deliverableArtifactId: d.id,
                                      uploadReportingDate,
                                    })
                                  }
                                />
                              )}
                            </Td>
                            <Td>
                              <IconButton
                                aria-label="more details"
                                size="sm"
                                icon={<Icon icon="chevron-right" />}
                                variant="ghost"
                                onClick={() => {
                                  setDrawerArtifact(d);
                                }}
                              />
                            </Td>
                          </Tr>
                          {!!errVal && errVal.errors.length > 0 && (
                            <Tr>
                              <Td colSpan={7}>
                                <DataTapeErrorView
                                  errors={errVal.errors}
                                  numOfRows={5}
                                />
                              </Td>
                            </Tr>
                          )}
                          {drawerArtifact && (
                            <DrawerView
                              creditFacilitySlug={container.creditFacilitySlug}
                              creditFacilityId={container.creditFacilityId}
                              organizationId={organizationId}
                              artifact={drawerArtifact}
                              isOpen={!!drawerArtifact}
                              onClose={() => setDrawerArtifact(undefined)}
                              dataTape={getDataTape(drawerArtifact.config)}
                              onSubmitDataTape={uploadFile}
                              errors={errorsMap[drawerArtifact.id]}
                            />
                          )}
                        </>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>
            <TabPanel>
              <Text>description goes here? is that it?</Text>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Box>
  );
};
